import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, User, CheckCircle, Wrench, Phone } from "@phosphor-icons/react";
import { BUSINESS } from "@/data/site";

export default function ApplianceInstallationGuide() {
  return (
    <article data-testid="guide-appliance-installation" className="ji-container py-16 md:py-24 max-w-4xl">
      <nav className="text-sm mb-8">
        <Link to="/" className="text-[var(--ji-muted)] hover:text-[var(--ji-accent)]">Home</Link>
        <span className="mx-2 text-[var(--ji-muted)]">·</span>
        <Link to="/services" className="text-[var(--ji-muted)] hover:text-[var(--ji-accent)]">Services</Link>
        <span className="mx-2 text-[var(--ji-muted)]">·</span>
        <span className="text-[var(--ji-text)]">Appliance Installation Best Practices</span>
      </nav>

      <div className="ji-overline mb-4 inline-flex items-center gap-2">
        <Wrench size={14} weight="bold" /> Service Guide · Appliance Repair and Installation
      </div>
      <h1
        style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }}
        className="text-4xl md:text-6xl font-bold tracking-tight"
      >
        Appliance Installation<br />
        <span className="text-[var(--ji-accent)] italic">Best Practices.</span>
      </h1>

      <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-[var(--ji-muted)] pb-6 border-b border-[var(--ji-border)]">
        <span className="inline-flex items-center gap-1.5"><User size={14} weight="bold" /> David Joseph · Joseph Industries</span>
        <span className="inline-flex items-center gap-1.5"><Clock size={14} weight="bold" /> 4 min read</span>
        <span>Southern Louisiana · New Orleans → Baton Rouge</span>
      </div>

      <div className="prose prose-lg max-w-none mt-8 space-y-6 text-[var(--ji-text)] leading-relaxed text-base md:text-lg">
        <p>
          A new appliance is only as good as the installation behind it. In two decades servicing kitchens and laundry rooms
          across Southern Louisiana, we&rsquo;ve replaced hundreds of appliances that were less than five years old &mdash;
          not because the equipment failed, but because the install did. Vents were kinked, water lines were pinched,
          drainage was reversed, or the machine was never leveled. Below is the playbook we use on every job, whether
          it&rsquo;s a Whirlpool refrigerator in Metairie or a Samsung range in Baton Rouge.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          Measure before you buy, not after it&rsquo;s on your porch
        </h2>
        <p>
          The single most common installation nightmare in this region is a fridge that won&rsquo;t clear the doorway or a
          range hood that doesn&rsquo;t match the cabinet cut-out. Older Louisiana homes &mdash; especially shotgun-style
          and pre-1970 builds &mdash; have non-standard framing. Before purchase, measure the appliance opening in three
          dimensions, then subtract one inch on every side for airflow and hose clearance. For refrigerators, measure the
          swing radius of the door against nearby countertops and cabinet handles. For over-the-range microwaves,
          confirm you have 66&Prime; from the finished floor to the bottom of the microwave, minimum.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          Utilities: shut off, verify, then connect
        </h2>
        <p>
          Every install begins with confirming the shut-off point &mdash; water valve behind the fridge, the 240V breaker
          for the dryer, the gas cock behind the range. New shut-off valves and stainless-steel braided lines cost less
          than $30 and are non-negotiable on any install we perform. For gas ranges, we always run a leak-test with soapy
          water on every fitting before ignition. Louisiana homes with older galvanized water lines often need a
          transition fitting to modern PEX or copper &mdash; skipping that step is how you end up with a slow drip inside
          a cabinet that rots the subfloor over 18 months.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          Level, secure, and test cycle
        </h2>
        <p>
          A washer that isn&rsquo;t level will wobble itself to death in three years. Every unit we install is checked with
          a bubble level front-to-back and side-to-side, feet adjusted to firm floor contact, and (for front-loaders)
          shipping bolts confirmed removed. Dryers get their vent hoods inspected for lint accumulation and their
          transitions clamped &mdash; not taped &mdash; because tape residue in a dryer vent is a fire risk we&rsquo;ve
          seen catch. We run a full test cycle on every washer, dryer, dishwasher, and ice maker before we leave. If it
          doesn&rsquo;t run right in front of us, it&rsquo;s not signed off.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          Warranty protection &amp; final walkthrough
        </h2>
        <p>
          Most manufacturers &mdash; Whirlpool, LG, Samsung, GE, KitchenAid, Bosch &mdash; require a professional
          installation record to honor the first-year warranty on parts affected by the install (compressors, motors,
          hoses). We provide a signed installation record with every job, along with the serial number, model, and
          install date &mdash; and we register the product with the manufacturer on the customer&rsquo;s behalf when they
          allow it. The final walkthrough covers filter locations, level-check confirmation, model-specific quirks
          (like the LG water-line loop that must be looped up, not down), and where to call if something drifts. A good
          install is invisible for the next ten years. That&rsquo;s the goal on every job.
        </p>
      </div>

      <div className="mt-16 ji-card p-8 md:p-10" style={{ borderRadius: 4, background: "var(--ji-primary)" }}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--ji-accent)] mb-2">Free quote · Louisiana licensed</div>
            <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight text-white">
              Need an appliance installed the right way?
            </h3>
            <p className="mt-3 text-white/80">
              We install refrigerators, washers, dryers, dishwashers, ranges, ovens, cooktops, over-the-range microwaves,
              and built-ins across the entire I-10 corridor. All major brands. Free estimates. $79 booking fee credited to
              your final bill.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link
              to="/contact"
              data-testid="guide-cta-quote"
              className="ji-btn-accent inline-flex items-center justify-center gap-2 h-12 px-5 text-sm font-semibold"
              style={{ borderRadius: 4 }}
            >
              Get a free quote <ArrowRight size={14} weight="bold" />
            </Link>
            <a
              href={`tel:${BUSINESS.primaryPhone.tel}`}
              data-testid="guide-cta-call"
              className="ji-btn-outline inline-flex items-center justify-center gap-2 h-12 px-5 text-sm font-semibold bg-white/10 text-white border-white/30 hover:bg-white/20"
              style={{ borderRadius: 4 }}
            >
              <Phone size={14} weight="bold" /> {BUSINESS.primaryPhone.display}
            </a>
          </div>
        </div>
      </div>

      <div className="mt-10 text-sm text-[var(--ji-muted)] flex flex-wrap gap-4">
        <span className="ji-overline">More guides:</span>
        <Link to="/guides/courier-logistics" className="hover:text-[var(--ji-accent)] inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Courier &amp; Last-Mile Logistics</Link>
        <Link to="/guides/home-preservation" className="hover:text-[var(--ji-accent)] inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Home Preservation &amp; Asset Protection</Link>
      </div>
    </article>
  );
}
